import type { QueryClient } from "@tanstack/react-query";
import { lowerFirst } from "lodash";
import { type FormValues, RELATION_DEFAULT_INSTANCE, type SubmitMeta } from "@/widgets/classic-form/form";
import {
    type FormMetadataResponse,
    GroupType,
    type RelationshipFieldGroup,
    ResponseItemType,
} from "@/entities/form-metadata";
import { getCategoryPlaceDataOptions, getDefaultPlaceExactness } from "@/entities/place";
import type { Person } from "@/entities/tree-person";
import { getPersonOptions } from "@/entities/tree-person";
import type { FormAPI } from "@/shared/forms";
import type { PageContext } from "@/shared/page-context";
import { getResetBaseFormValues } from "./getResetBaseFormValues";
import { transformPersonToFormValues } from "./transformPerson";

/**
 * Gives every prefilled-static family-member row at least one instance after quick-fill, so it
 * doesn't disappear with an empty instances array. Events need no such pass: `getEventsDetails`
 * already pads and floors its own rows.
 */
export const ensureNonRemovableDefaults = (values: FormValues, formMetadata: FormMetadataResponse): void => {
    for (const item of formMetadata.fieldGroupsAndFeatureContainers) {
        if (item.type !== ResponseItemType.FieldGroup) continue;
        if (item.groupType !== GroupType.FamilyMember) continue;

        const familyGroup = item as RelationshipFieldGroup;
        if (familyGroup.fieldsetDisplayStrategy === "empty-dynamic") continue;
        if (familyGroup.fieldsetDisplayStrategy === "prefilled-dynamic") continue;

        for (const fieldset of familyGroup.fieldsets) {
            const key = lowerFirst(fieldset.relation) as keyof FormValues["family"];
            if (values.family[key]?.instances.length === 0) {
                values.family[key].instances = [RELATION_DEFAULT_INSTANCE];
            }
        }
    }
};

export interface ApplyPersonToFormArgs {
    personId: string;
    treeId: string;
    queryClient: QueryClient;
    formApi: FormAPI<FormValues, SubmitMeta>;
    formMetadata: FormMetadataResponse;
    defaultFormValues: FormValues;
    pageContext: PageContext;
    isEditForm: boolean;
}

/**
 * Resolves the unique gpids across an event's instances (birth and death often
 * share a place) and patches `locationExactness` with the computed level-based
 * default. Quickfill writes places via `formApi.setValues` and never goes through
 * `PlaceAutocompleteField`, so it never publishes to `placeSelectionPubSub` and
 * must compute its own default here.
 *
 * Uses `Promise.allSettled`, not `Promise.all`: a rejected fetch for one gpid
 * must not sink the whole quickfill selection into the caller's catch block.
 * A failed or empty-result (`[]`) lookup leaves `locationExactness: null`.
 */
const applyPlaceProximityDefaults = async (
    events: FormValues["events"],
    queryClient: QueryClient,
    culture: string,
): Promise<void> => {
    const gpids = new Set<string>();
    for (const event of Object.values(events)) {
        for (const instance of event.instances) {
            if (instance.gpid) gpids.add(instance.gpid);
        }
    }
    if (gpids.size === 0) return;

    const results = await Promise.allSettled(
        Array.from(gpids).map(async (gpid) => {
            const places = await queryClient.ensureQueryData(getCategoryPlaceDataOptions({ gpid, culture }));
            return [gpid, places[0]] as const;
        }),
    );

    const exactnessByGpid = new Map<string, ReturnType<typeof getDefaultPlaceExactness>>();
    for (const result of results) {
        if (result.status !== "fulfilled") continue;
        const [gpid, place] = result.value;
        if (!place) continue;
        exactnessByGpid.set(gpid, getDefaultPlaceExactness(place.LevelId, place.IdHierarchy));
    }

    for (const event of Object.values(events)) {
        for (const instance of event.instances) {
            if (instance.gpid && exactnessByGpid.has(instance.gpid)) {
                instance.locationExactness = exactnessByGpid.get(instance.gpid)!;
            }
        }
    }
};

/**
 * The quick-fill "apply person" core: fetches the person (cache-first), builds a
 * reset base from the default values, transforms the person into form values and
 * applies them to the mounted form as a DRAFT via `formApi.setValues`.
 *
 * Deliberately takes `formApi` and its sibling deps as arguments so it can be
 * called both from inside the `<Form>` tree (useQuickFillSelection) and from
 * outside it (ClassicForm's imperative `applyPerson` handle, where `useFormAPI`
 * is not available).
 *
 * The reset base is `defaultFormValues` on the main search form, but on the edit
 * form it is an empty-sourced baseline (see `getResetBaseFormValues`) so prior
 * search values the selected person does not provide are cleared rather than
 * carried over.
 *
 * Does not announce or handle errors — callers own those concerns.
 */
export const applyPersonToForm = async ({
    personId,
    treeId,
    queryClient,
    formApi,
    formMetadata,
    defaultFormValues,
    pageContext,
    isEditForm,
}: ApplyPersonToFormArgs): Promise<Person> => {
    const { defaultYearProximity, defaultPlaceProximityEnabled } = pageContext.runtimeConfig;
    const person = await queryClient.ensureQueryData(getPersonOptions({ personId, treeId }));

    const currentValues = formApi.getValues();

    const base = getResetBaseFormValues({ isEditForm, formMetadata, defaultFormValues, defaultYearProximity });

    const transformed = transformPersonToFormValues(person, base, formMetadata, currentValues, defaultYearProximity);

    ensureNonRemovableDefaults(transformed, formMetadata);

    if (defaultPlaceProximityEnabled) {
        await applyPlaceProximityDefaults(transformed.events, queryClient, pageContext.locale);
    }

    formApi.setValues(transformed);
    return person;
};
